import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import config from "../config";
import Slideshow from "./Slideshow";
import VideoSuppliersSection from "./VideoSuppliersSection";
import ServicesSection from "./ServicesSection";
import Footer from "./Footer";

const { API_BASE, UPLOADS_URL, SHOP_UPLOADS_URL } = config;

async function fetchJSON(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status} – ${url}`);
  return res.json();
}

async function apiFetch(url, token, options = {}) {
  const res = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
}

function formatPrice(val) {
  if (val === null || val === undefined) return null;
  return "R" + Number(val).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/* ── Build WhatsApp enquiry link (mirrors ShopPage) ── */
function buildWhatsAppLink(phoneNumber, productName, productSlug) {
  if (!phoneNumber) return null;
  const cleaned = phoneNumber.replace(/\D/g, "");
  const wa = cleaned.startsWith("0") ? "27" + cleaned.slice(1) : cleaned;
  const productUrl = `${window.location.origin}/shop/${productSlug}`;
  const msg = `Hi, I'm interested in the *${productName}* and would like to enquire about pricing and availability.\n\nProduct link: ${productUrl}`;
  return `https://wa.me/${wa}?text=${encodeURIComponent(msg)}`;
}

/* ── Fisher-Yates shuffle (unbiased random sample for the homepage teaser) ── */
function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const OBJECTIVE_ICONS = [
  "emergency-repair.svg",
  "ac-repair.svg",
  "commercial.svg",
  "heating.svg",
  "cooler.svg",
];

function parseObjectives(raw) {
  if (!raw) return [];
  return raw
    .split("\n")
    .map((line) => line.replace(/^\s*[-•]\s*/, "").trim())
    .filter(Boolean)
    .map((line) => {
      const colonIdx = line.indexOf(":");
      if (colonIdx === -1) return { title: line, desc: "" };
      return {
        title: line.slice(0, colonIdx).trim(),
        desc: line.slice(colonIdx + 1).trim(),
      };
    });
}

function truncateWords(text, limit) {
  if (!text) return { short: "", truncated: false };
  const words = text.trim().split(/\s+/);
  if (words.length <= limit) return { short: text, truncated: false };
  return { short: words.slice(0, limit).join(" ") + "...", truncated: true };
}

/* ─────────────────────────────────────────────
   WHY CHOOSE US
───────────────────────────────────────────── */
function WhyChooseUs({ aboutUs }) {
  const objectives = parseObjectives(aboutUs?.objectives);
  const { short: introText, truncated } = truncateWords(aboutUs?.content, 50);

  const img0 = aboutUs?.image1 ? `${UPLOADS_URL}/about/${aboutUs.image1}` : null;
  const img1 = aboutUs?.image2 ? `${UPLOADS_URL}/about/${aboutUs.image2}` : null;
  const img2 = aboutUs?.image3 ? `${UPLOADS_URL}/about/${aboutUs.image3}` : null;

  return (
    <section className="wcu">
      <div className="wcu__inner">
        <div className="wcu__left">
          <span className="wcu__pill">
            <img
              src={`${UPLOADS_URL}/svg-icons/emergency-repair.svg`}
              alt=""
              aria-hidden="true"
              className="wcu__pill-icon"
            />
            Why Choose Us
          </span>
          <h2 className="wcu__heading">{aboutUs?.title || "About Us"}</h2>
          <p className="wcu__intro">{introText}</p>
          {truncated && (
            <a href="/about-us" className="wcu__readmore">
              Read More <span aria-hidden="true">→</span>
            </a>
          )}
          {objectives.length > 0 && (
            <div className="wcu__cards">
              {objectives.map((obj, i) => (
                <div className="wcu__card" key={i}>
                  <span className="wcu__card-num" aria-hidden="true">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="wcu__card-icon">
                    <img
                      src={`${UPLOADS_URL}/svg-icons/${OBJECTIVE_ICONS[i % OBJECTIVE_ICONS.length]}`}
                      alt=""
                      aria-hidden="true"
                    />
                  </div>
                  <div className="wcu__card-body">
                    <h3 className="wcu__card-title">{obj.title}</h3>
                    <p className="wcu__card-desc">{obj.desc}</p>
                  </div>
                  {i < objectives.length - 1 && (
                    <div className="wcu__card-divider" aria-hidden="true" />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="wcu__right">
          <div className="wcu__img-stack">
            <div className="wcu__img-wrap wcu__img-wrap--primary">
              {img0 ? (
                <img src={img0} alt="Our team at work" className="wcu__img" />
              ) : (
                <div className="wcu__img wcu__img--placeholder" />
              )}
            </div>
            <div className="wcu__img-wrap wcu__img-wrap--secondary">
              {img1 ? (
                <img src={img1} alt="Professional service" className="wcu__img" />
              ) : (
                <div className="wcu__img wcu__img--placeholder" />
              )}
            </div>
            <div className="wcu__img-wrap wcu__img-wrap--tertiary">
              {img2 ? (
                <img src={img2} alt="Quality workmanship" className="wcu__img" />
              ) : (
                <div className="wcu__img wcu__img--placeholder" />
              )}
            </div>
            <div className="wcu__dots" aria-hidden="true" />
          </div>
          <div className="wcu__trust">
            <div className="wcu__trust-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 6L9 17L4 12" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="wcu__trust-text">
              <strong>Trusted by Our Customers</strong>
              <span>Quality repairs with guaranteed satisfaction – every time.</span>
            </div>
            <a href="/contact-us" className="wcu__trust-btn">
              BOOK HERE <span aria-hidden="true">→</span>
            </a>
          </div>
        </div>
      </div>

      <style>{`
        .wcu { padding: 80px 0; background: #fff; overflow: hidden; }
        .wcu__inner {
          max-width: 1280px; margin: 0 auto; padding: 0 40px;
          display: grid; grid-template-columns: 1fr 1fr;
          align-items: center; gap: 60px;
        }
        .wcu__pill {
          display: inline-flex; align-items: center; gap: 8px;
          background: #fff3ec; color: #f76b1c;
          font-size: 13px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase;
          padding: 8px 16px; border-radius: 999px; margin-bottom: 18px;
        }
        .wcu__pill-icon { width: 18px; height: 18px; object-fit: contain; filter: invert(47%) sepia(97%) saturate(1200%) hue-rotate(357deg) brightness(103%) contrast(97%); }
        .wcu__heading {
          font-size: 38px; line-height: 1.2; font-weight: 800;
          color: #013d97; margin-bottom: 20px; letter-spacing: -.5px;
        }
        .wcu__intro { font-size: 14.5px; line-height: 1.8; color: #5a5f72; margin-bottom: 16px; max-width: 520px; text-align: justify; }
        .wcu__readmore {
          display: inline-flex; align-items: center; gap: 6px;
          color: #f76b1c; font-size: 14px; font-weight: 700;
          text-decoration: none; margin-bottom: 32px; transition: gap 0.2s;
        }
        .wcu__readmore:hover { gap: 10px; }
        .wcu__cards { display: flex; flex-direction: column; gap: 0; }
        .wcu__card {
          position: relative; background: #fff8f4; border-radius: 14px;
          padding: 12px 22px 12px 18px;
          display: flex; align-items: flex-start; gap: 16px; overflow: hidden;
        }
        .wcu__card-num {
          position: absolute; right: 16px; top: 50%; transform: translateY(-50%);
          font-size: 64px; font-weight: 900; color: rgba(247,107,28,.08);
          line-height: 1; pointer-events: none; user-select: none;
        }
        .wcu__card-icon {
          flex-shrink: 0; width: 40px; height: 40px; border-radius: 50%;
          background: #f76b1c; display: flex; align-items: center; justify-content: center;
          box-shadow: 0 6px 18px rgba(247,107,28,.35);
        }
        .wcu__card-icon img { width: 20px; height: 20px; object-fit: contain; filter: brightness(0) invert(1); }
        .wcu__card-body { flex: 1; min-width: 0; position: relative; z-index: 1; }
        .wcu__card-title { font-size: 16px; font-weight: 700; color: #013d97; margin-bottom: 6px; }
        .wcu__card-desc { font-size: 13.5px; line-height: 1.7; color: #5a5f72; }
        .wcu__card-divider {
          position: absolute; bottom: 0; left: 18px; right: 18px;
          height: 1px; background: rgba(247,107,28,.18);
        }
        .wcu__right { display: flex; flex-direction: column; align-items: center; gap: 20px; }
        .wcu__img-stack { position: relative; width: 100%; max-width: 520px; height: 500px; }
        .wcu__img-wrap { position: absolute; border-radius: 20px; overflow: hidden; }
        .wcu__img-wrap--primary {
          width: 62%; aspect-ratio: 4/5; top: 0; left: 0;
          transform: rotate(-3deg); z-index: 1;
          box-shadow: 0 20px 60px rgba(1,61,151,.18);
        }
        .wcu__img-wrap--secondary {
          width: 50%; aspect-ratio: 1/1; bottom: 0; right: 0;
          transform: rotate(3deg); z-index: 3;
          border: 5px solid #fff; box-shadow: 0 24px 70px rgba(1,61,151,.22);
        }
        .wcu__img-wrap--tertiary {
          width: 44%; aspect-ratio: 1/1; top: 10px; right: 4%;
          transform: rotate(2deg); z-index: 2;
          border: 5px solid #fff; box-shadow: 0 14px 40px rgba(1,61,151,.16);
        }
        .wcu__img { width: 100%; height: 100%; object-fit: cover; object-position: center top; display: block; }
        .wcu__img--placeholder { width: 100%; height: 100%; background: linear-gradient(135deg, #e6f4fc 0%, #cce0f5 100%); }
        .wcu__dots {
          position: absolute; bottom: -20px; left: -20px;
          width: 120px; height: 120px; z-index: 0;
          background-image: radial-gradient(#f76b1c 1.5px, transparent 1.6px);
          background-size: 12px 12px; opacity: .35;
        }
        .wcu__trust {
          width: 100%; max-width: 520px;
          display: flex; align-items: center; gap: 14px;
          background: #fff; border: 1.5px dashed #e8b4b8;
          border-radius: 14px; padding: 16px 20px;
        }
        .wcu__trust-icon {
          flex-shrink: 0; width: 44px; height: 44px;
          border-radius: 50%; background: #c0392b;
          display: flex; align-items: center; justify-content: center;
        }
        .wcu__trust-icon svg { width: 22px; height: 22px; }
        .wcu__trust-text { flex: 1; display: flex; flex-direction: column; gap: 2px; }
        .wcu__trust-text strong { font-size: 14px; font-weight: 700; color: #1a1a2e; }
        .wcu__trust-text span { font-size: 13px; color: #5a5f72; line-height: 1.5; }
        .wcu__trust-btn {
          flex-shrink: 0; display: inline-flex; align-items: center; gap: 8px;
          background: #029add; color: #fff; font-size: 14px; font-weight: 700;
          padding: 12px 22px; border-radius: 8px; text-decoration: none;
          transition: background 0.2s; white-space: nowrap;
        }
        .wcu__trust-btn:hover { background: #0180b8; }
        @media (max-width: 960px) {
          .wcu__inner { grid-template-columns: 1fr; gap: 40px; }
          .wcu__right { order: -1; }
          .wcu__img-stack { height: 380px; max-width: 100%; }
          .wcu__heading { font-size: 28px; }
          .wcu__trust { max-width: 100%; }
        }
        @media (max-width: 600px) {
          .wcu { padding: 50px 0; }
          .wcu__inner { padding: 0 20px; }
          .wcu__img-stack { height: 300px; }
          .wcu__heading { font-size: 24px; }
          .wcu__card-num { font-size: 44px; }
          .wcu__trust { flex-wrap: wrap; }
          .wcu__trust-btn { width: 100%; justify-content: center; }
        }
      `}</style>
    </section>
  );
}

/* ─────────────────────────────────────────────
   SHARED RESPONSIVE PER-PAGE HOOK
   maxPerPage controls how many cards show on wide
   screens (desktop). Mobile/tablet breakpoints are
   shared across every carousel that uses this hook.
───────────────────────────────────────────── */
function useResponsivePerPage(maxPerPage = 3) {
  const [perPage, setPerPage] = useState(maxPerPage);
  useEffect(() => {
    const calc = () => {
      const w = window.innerWidth;
      if (w <= 600) setPerPage(1);
      else if (w <= 960) setPerPage(2);
      else setPerPage(maxPerPage);
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, [maxPerPage]);
  return perPage;
}

/* ─────────────────────────────────────────────
   PRODUCTS CAROUSEL (supplier / brochure cards)
───────────────────────────────────────────── */
function ProductsCarousel() {
  const [products, setProducts]   = useState([]);
  const [current, setCurrent]     = useState(0);
  const [cardWidth, setCardWidth] = useState(0);
  const timerRef   = useRef(null);
  const trackRef   = useRef(null);
  const firstCard  = useRef(null);

  const PER_PAGE = useResponsivePerPage();
  const GAP      = 20;

  useEffect(() => {
    fetchJSON(`${API_BASE}/products.php`)
      .then((data) => setProducts(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Products carousel load error:", err));
  }, []);

  useEffect(() => { setCurrent(0); }, [PER_PAGE]);

  useEffect(() => {
    if (!firstCard.current) return;
    const measure = () => { if (firstCard.current) setCardWidth(firstCard.current.offsetWidth); };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(firstCard.current);
    return () => ro.disconnect();
  }, [products, PER_PAGE]);

  const maxStart = Math.max(0, products.length - PER_PAGE);

  const startTimer = useCallback(() => {
    clearInterval(timerRef.current);
    if (products.length <= PER_PAGE) return;
    timerRef.current = setInterval(() => {
      setCurrent((c) => (c < maxStart ? c + 1 : 0));
    }, 3500);
  }, [products.length, PER_PAGE, maxStart]);

  useEffect(() => { startTimer(); return () => clearInterval(timerRef.current); }, [startTimer]);

  const goTo = useCallback(
    (idx) => { setCurrent(Math.max(0, Math.min(idx, maxStart))); startTimer(); },
    [maxStart, startTimer]
  );

  if (products.length === 0) return null;
  const offsetPx = current * (cardWidth + GAP);

  return (
    <section className="pc">
      <div className="pc__inner">
        <div className="pc__header">
          <div>
            <span className="pc__eyebrow">
              Our Products
              <span className="pc__eyebrow-line" aria-hidden="true" />
            </span>
            <h2 className="pc__heading">Browse Our <em>Product Range</em></h2>
          </div>
          <div className="pc__nav">
            <button className="pc__btn" onClick={() => goTo(current - 1)} disabled={current === 0} aria-label="Previous products">←</button>
            <button className="pc__btn" onClick={() => goTo(current + 1)} disabled={current === maxStart} aria-label="Next products">→</button>
          </div>
        </div>

        <div className="pc__track-wrap">
          <div ref={trackRef} className="pc__track" style={{ transform: `translateX(-${offsetPx}px)` }}>
            {products.map((p, idx) => {
              const imgSrc = p.image_filename ? `${UPLOADS_URL}/products/${p.image_filename}` : null;
              return (
                <div className="pc__card" key={p.id} ref={idx === 0 ? firstCard : null}
                  style={{ "--per-page": PER_PAGE, "--gap": `${GAP}px` }}>
                  <div className="pc__card-media">
                    {imgSrc ? (
                      <img src={imgSrc} alt={p.title} className="pc__card-img"
                        onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    ) : (
                      <div className="pc__card-img pc__card-img--fallback" />
                    )}
                    <div className="pc__card-scrim" />
                    <div className="pc__card-body">
                      {p.supplier && <span className="pc__card-supplier">{p.supplier}</span>}
                      <div className="pc__card-title">{p.title}</div>
                      <div className="pc__card-footer">
                        <a href="/products" className="pc__card-btn pc__card-btn--view">View Details</a>
                        {p.brochure_url && (
                          <a href={p.brochure_url} target="_blank" rel="noopener noreferrer"
                            className="pc__card-btn pc__card-btn--brochure">Brochure</a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {maxStart > 0 && (
          <div className="pc__dots-wrap" role="tablist" aria-label="Product slides">
            {Array.from({ length: maxStart + 1 }).map((_, i) => (
              <button key={i} className={`pc__dot${i === current ? " pc__dot--active" : ""}`}
                onClick={() => goTo(i)} aria-label={`Go to slide ${i + 1}`} aria-selected={i === current} role="tab" />
            ))}
          </div>
        )}

        <div className="pc__view-all-wrap">
          <a href="/products" className="pc__view-all">View All Products <span aria-hidden="true">→</span></a>
        </div>
      </div>

      <style>{`
        .pc { padding: 72px 0; background: #f5f7fa; overflow: hidden; }
        .pc__inner { max-width: 1280px; margin: 0 auto; padding: 0 40px; }
        .pc__header { display: flex; align-items: flex-end; justify-content: space-between; margin-bottom: 36px; }
        .pc__eyebrow { display: flex; align-items: center; gap: 10px; font-size: 13px; font-weight: 700; color: #029add; letter-spacing: .1em; text-transform: uppercase; margin-bottom: 14px; }
        .pc__eyebrow-line { display: inline-block; width: 36px; height: 2px; background: #029add; flex-shrink: 0; }
        .pc__heading { font-size: clamp(26px, 2.8vw, 38px); font-weight: 900; color: #013d97; line-height: 1.18; letter-spacing: -.5px; margin: 0; }
        .pc__heading em { font-style: normal; color: #029add; }
        .pc__nav { display: flex; gap: 10px; flex-shrink: 0; margin-bottom: 6px; }
        .pc__btn { width: 44px; height: 44px; border-radius: 50%; border: 2px solid #013d97; background: #fff; color: #013d97; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 18px; transition: background .2s, color .2s; }
        .pc__btn:hover:not(:disabled) { background: #013d97; color: #fff; }
        .pc__btn:disabled { opacity: .35; cursor: default; pointer-events: none; }
        .pc__track-wrap { overflow: hidden; width: 100%; }
        .pc__track { display: flex; gap: 20px; transition: transform .5s cubic-bezier(.4,0,.2,1); width: max-content; }
        .pc__card {
          --pc-pad: 80px;
          width: min(
            calc((1280px - var(--pc-pad) - (var(--per-page,3) - 1) * var(--gap,20px)) / var(--per-page,3)),
            calc((100vw - var(--pc-pad) - (var(--per-page,3) - 1) * var(--gap,20px)) / var(--per-page,3))
          );
          flex-shrink: 0; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 18px rgba(1,21,70,.1);
        }
        .pc__card-media { position: relative; aspect-ratio: 3/4; overflow: hidden; background: #013d97; }
        .pc__card-img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform .5s ease; }
        .pc__card-img--fallback { background: linear-gradient(135deg, #013d97, #029add); }
        .pc__card:hover .pc__card-img { transform: scale(1.06); }
        .pc__card-scrim { position: absolute; inset: 0; background: linear-gradient(to top, rgba(2,4,12,.9) 0%, rgba(2,4,12,.45) 30%, transparent 70%); }
        .pc__card-body { position: absolute; bottom: 0; left: 0; right: 0; padding: 20px 22px; }
        .pc__card-supplier { display: inline-block; background: rgba(255,255,255,.15); border: 1px solid rgba(255,255,255,.3); color: #fff; font-size: 10px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; padding: 4px 10px; border-radius: 20px; margin-bottom: 8px; }
        .pc__card-title { font-size: 17px; font-weight: 800; color: #fff; line-height: 1.25; margin-bottom: 10px; }
        .pc__card-footer { display: flex; gap: 8px; border-top: 1px solid rgba(255,255,255,.15); padding-top: 10px; }
        .pc__card-btn { flex: 1; display: flex; align-items: center; justify-content: center; gap: 6px; padding: 9px 12px; border-radius: 6px; font-size: 11px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; text-decoration: none; transition: background .2s; }
        .pc__card-btn--view { background: rgba(255,255,255,.18); color: #fff; }
        .pc__card-btn--view:hover { background: rgba(255,255,255,.3); }
        .pc__card-btn--brochure { background: #029add; color: #fff; }
        .pc__card-btn--brochure:hover { background: #0180b8; }
        .pc__dots-wrap { display: flex; gap: 8px; justify-content: center; margin-top: 28px; }
        .pc__dot { width: 8px; height: 8px; border-radius: 50%; background: #d0d8ec; border: none; cursor: pointer; padding: 0; transition: background .2s, transform .2s; }
        .pc__dot--active { background: #013d97; transform: scale(1.3); }
        .pc__view-all-wrap { text-align: center; margin-top: 32px; }
        .pc__view-all { display: inline-flex; align-items: center; gap: 8px; background: #013d97; color: #fff; font-size: 13px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; padding: 13px 28px; text-decoration: none; transition: background .2s; }
        .pc__view-all:hover { background: #022d75; }
        @media (max-width: 600px) {
          .pc { padding: 50px 0; }
          .pc__inner { padding: 0 20px; }
          .pc__card { --pc-pad: 40px; }
          .pc__header { flex-direction: column; align-items: flex-start; gap: 16px; }
          .pc__nav { align-self: flex-end; }
        }
      `}</style>
    </section>
  );
}

/* ─────────────────────────────────────────────
   SHOP PRODUCT CARD  (mirrors ShopPage card)
   Now option-aware for stock/price so products
   with size/variant options (BTU, KG, M, KW, M2)
   don't incorrectly show as "Out of stock" —
   see shop-product.php / ProductDetailPage.jsx
   for the source-of-truth logic this mirrors.
───────────────────────────────────────────── */
function ShopProductCard({ product, wishlist, onWishlistToggle, onAddToCart, waPhone }) {
  const navigate = useNavigate();
  const inWishlist = wishlist.includes(product.id);

  // ── Option-aware stock ──
  // For products with variants, `shop_products.quantity` is not the real
  // stock figure — each row in shop_product_btu_options carries its own
  // quantity. Sum those instead, exactly like ProductDetailPage does when
  // it computes `effectiveQuantity` per selected option.
  const options = product.options || [];
  const hasOptions = options.length > 0;
  const effectiveQuantity = hasOptions
    ? options.reduce((sum, o) => sum + (Number(o.quantity) || 0), 0)
    : Number(product.quantity) || 0;

  const hasDiscount = product.sale_price && Number(product.sale_price) < Number(product.price);
  const img = product.image1;
  const isOutOfStock = effectiveQuantity === 0;
  const isLowStock = effectiveQuantity > 0 && effectiveQuantity <= (product.low_stock_alert || 5);

  const waLink = waPhone ? buildWhatsAppLink(waPhone, product.name, product.slug) : null;

  const handleCtaClick = (e) => {
    e.stopPropagation();
    if (isOutOfStock) return;
    if (hasOptions) {
      // Can't add a specific size/variant without the shopper choosing one —
      // send them to the product page where the size selector lives.
      navigate(`/shop/${product.slug}`);
      return;
    }
    onAddToCart(product);
  };

  return (
    <div
      className="spc__card"
      onClick={() => navigate(`/shop/${product.slug}`)}
    >
      <div className="spc__card-media">
        {img ? (
          <img
            src={`${SHOP_UPLOADS_URL}/${img}`}
            alt={product.name}
            className="spc__card-img"
            loading="lazy"
          />
        ) : (
          <div className="spc__card-img-fallback">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" aria-hidden="true">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M3 9l4-4 4 4 4-5 4 5" />
            </svg>
          </div>
        )}

        {/* Badges */}
        <div className="spc__badges">
          {product.is_featured && <span className="spc__badge spc__badge--featured">Featured</span>}
          {hasDiscount && <span className="spc__badge spc__badge--sale">−{product.discount_percent}%</span>}
          {isOutOfStock && <span className="spc__badge spc__badge--out">Out of stock</span>}
          {isLowStock && <span className="spc__badge spc__badge--low">Low stock</span>}
        </div>

        {/* Wishlist */}
        <button
          className={`spc__wish ${inWishlist ? "spc__wish--active" : ""}`}
          onClick={(e) => { e.stopPropagation(); onWishlistToggle(product.id); }}
          aria-label={inWishlist ? "Remove from wishlist" : "Add to wishlist"}
        >
          <svg width="16" height="16" viewBox="0 0 24 24"
            fill={inWishlist ? "#ef4444" : "none"}
            stroke={inWishlist ? "#ef4444" : "currentColor"}
            strokeWidth="2" aria-hidden="true">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
        </button>
      </div>

      <div className="spc__card-body">
        {product.brand && <p className="spc__brand">{product.brand}</p>}
        <h3 className="spc__name">{product.name}</h3>
        {product.energy_rating && (
          <p className="spc__spec">
            Energy: <strong>{product.energy_rating}</strong>
            {product.capacity_kw && <> · {product.capacity_kw} kW</>}
            {product.coverage_area_m2 && <> · {product.coverage_area_m2} m²</>}
          </p>
        )}
        <div className="spc__price-row">
          {hasDiscount ? (
            <>
              <span className="spc__price spc__price--sale">{hasOptions ? "From " : ""}{formatPrice(product.sale_price)}</span>
              <span className="spc__price spc__price--orig">{formatPrice(product.price)}</span>
            </>
          ) : (
            <span className="spc__price">{hasOptions ? "From " : ""}{formatPrice(product.price) ?? "Contact us"}</span>
          )}
        </div>

        {/* ── Split CTA row: Add to Cart / Select options + WhatsApp icon ── */}
        <div className="spc__cta-row" onClick={(e) => e.stopPropagation()}>
          <button
            className="spc__cta"
            disabled={isOutOfStock}
            onClick={handleCtaClick}
            aria-label={hasOptions ? `View options for ${product.name}` : `Add ${product.name} to cart`}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
            </svg>
            {isOutOfStock ? "Out of stock" : hasOptions ? "Select options" : "Add to cart"}
          </button>

          {waLink && (
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="spc__wa-btn"
              aria-label={`Enquire about ${product.name} on WhatsApp`}
              title="Enquire on WhatsApp"
              onClick={(e) => e.stopPropagation()}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                <path d="M12 0C5.373 0 0 5.373 0 12c0 2.117.554 4.103 1.523 5.824L.057 23.928a.5.5 0 0 0 .6.633l6.313-1.434A11.94 11.94 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 0 1-5.001-1.369l-.358-.214-3.715.844.877-3.609-.234-.371A9.818 9.818 0 1 1 12 21.818z"/>
              </svg>
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function ShopProductsCarousel({ isLoggedIn, token, onAddToCart, onOpenAuth }) {
  const [products, setProducts] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [contact, setContact]   = useState(null); // company contact for WhatsApp
  const [current, setCurrent]   = useState(0);
  const [cardWidth, setCardWidth] = useState(0);
  const timerRef  = useRef(null);
  const firstCard = useRef(null);

  // Homepage teaser shows up to 4 cards at a time on desktop
  // (mobile/tablet breakpoints still fall back to 1 / 2 via the shared hook).
  const PER_PAGE = useResponsivePerPage(4);
  const GAP      = 20;

  // How many products to feature on the homepage carousel — the full
  // catalog is only shown on the dedicated Shop page ("View All" below).
  const HOMEPAGE_LIMIT = 6;

  /* Load shop products — pick a random sample of HOMEPAGE_LIMIT for the teaser */
  useEffect(() => {
    fetchJSON(`${API_BASE}/shop-products.php`)
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        const sample = shuffleArray(list).slice(0, HOMEPAGE_LIMIT);
        setProducts(sample);
      })
      .catch((err) => console.error("Shop products carousel error:", err));
  }, []);

  /* Fetch company contact for WhatsApp */
  useEffect(() => {
    fetchJSON(`${API_BASE}/company-contact.php`)
      .then((data) => setContact(data || null))
      .catch(() => {});
  }, []);

  /* Load wishlist when logged in */
  useEffect(() => {
    if (!isLoggedIn || !token) { setWishlist([]); return; }
    apiFetch(`${API_BASE}/customer-wishlist.php`, token)
      .then((data) => setWishlist((data.wishlist || []).map((p) => p.id)))
      .catch(() => setWishlist([]));
  }, [isLoggedIn, token]);

  useEffect(() => { setCurrent(0); }, [PER_PAGE]);

  /* Measure first card */
  useEffect(() => {
    if (!firstCard.current) return;
    const measure = () => { if (firstCard.current) setCardWidth(firstCard.current.offsetWidth); };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(firstCard.current);
    return () => ro.disconnect();
  }, [products, PER_PAGE]);

  const maxStart = Math.max(0, products.length - PER_PAGE);

  const startTimer = useCallback(() => {
    clearInterval(timerRef.current);
    if (products.length <= PER_PAGE) return;
    timerRef.current = setInterval(() => {
      setCurrent((c) => (c < maxStart ? c + 1 : 0));
    }, 4000);
  }, [products.length, PER_PAGE, maxStart]);

  useEffect(() => { startTimer(); return () => clearInterval(timerRef.current); }, [startTimer]);

  const goTo = useCallback(
    (idx) => { setCurrent(Math.max(0, Math.min(idx, maxStart))); startTimer(); },
    [maxStart, startTimer]
  );

  const handleWishlistToggle = useCallback((id) => {
    if (!isLoggedIn || !token) { onOpenAuth?.("login"); return; }
    apiFetch(`${API_BASE}/customer-wishlist.php`, token, {
      method: "POST",
      body: JSON.stringify({ product_id: id }),
    })
      .then((data) => {
        setWishlist((prev) =>
          data.action === "added" ? [...prev, id] : prev.filter((x) => x !== id)
        );
      })
      .catch((e) => alert("Could not update wishlist: " + e.message));
  }, [isLoggedIn, token, onOpenAuth]);

  if (products.length === 0) return null;

  const offsetPx = current * (cardWidth + GAP);

  return (
    <section className="spc">
      <div className="spc__inner">

        {/* Header — mirrors ProductsCarousel */}
        <div className="spc__header">
          <div>
            <span className="spc__eyebrow">
              Shop Products
              <span className="spc__eyebrow-line" aria-hidden="true" />
            </span>
            <h2 className="spc__heading">
              Browse Our <em>Shop Range</em>
            </h2>
          </div>
          <div className="spc__nav">
            <button className="spc__btn" onClick={() => goTo(current - 1)} disabled={current === 0} aria-label="Previous shop products">←</button>
            <button className="spc__btn" onClick={() => goTo(current + 1)} disabled={current === maxStart} aria-label="Next shop products">→</button>
          </div>
        </div>

        {/* Track */}
        <div className="spc__track-wrap">
          <div
            className="spc__track"
            style={{ transform: `translateX(-${offsetPx}px)` }}
          >
            {products.map((p, idx) => (
              <div
                key={p.id}
                ref={idx === 0 ? firstCard : null}
                className="spc__track-item"
                style={{ "--per-page": PER_PAGE, "--gap": `${GAP}px` }}
              >
                <ShopProductCard
                  product={p}
                  wishlist={wishlist}
                  onWishlistToggle={handleWishlistToggle}
                  onAddToCart={onAddToCart}
                  waPhone={contact?.phone_number || null}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Dots */}
        {maxStart > 0 && (
          <div className="spc__dots-wrap" role="tablist" aria-label="Shop product slides">
            {Array.from({ length: maxStart + 1 }).map((_, i) => (
              <button
                key={i}
                className={`spc__dot${i === current ? " spc__dot--active" : ""}`}
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
                aria-selected={i === current}
                role="tab"
              />
            ))}
          </div>
        )}

        {/* View all — links to the full Shop page, which loads the entire catalog independently */}
        <div className="spc__view-all-wrap">
          <a href="/shop" className="spc__view-all">
            View All Shop Products <span aria-hidden="true">→</span>
          </a>
        </div>
      </div>

      <style>{`
        /* ── Shop Products Carousel ── */
        .spc { padding: 72px 0; background: #f5f7fa; overflow: hidden; }
        .spc__inner { max-width: 1280px; margin: 0 auto; padding: 0 40px; }

        .spc__header { display: flex; align-items: flex-end; justify-content: space-between; margin-bottom: 36px; }
        .spc__eyebrow { display: flex; align-items: center; gap: 10px; font-size: 13px; font-weight: 700; color: #f76b1c; letter-spacing: .1em; text-transform: uppercase; margin-bottom: 14px; }
        .spc__eyebrow-line { display: inline-block; width: 36px; height: 2px; background: #f76b1c; flex-shrink: 0; }
        .spc__heading { font-size: clamp(26px, 2.8vw, 38px); font-weight: 900; color: #013d97; line-height: 1.18; letter-spacing: -.5px; margin: 0; }
        .spc__heading em { font-style: normal; color: #f76b1c; }

        .spc__nav { display: flex; gap: 10px; flex-shrink: 0; margin-bottom: 6px; }
        .spc__btn { width: 44px; height: 44px; border-radius: 50%; border: 2px solid #013d97; background: #fff; color: #013d97; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 18px; transition: background .2s, color .2s; }
        .spc__btn:hover:not(:disabled) { background: #013d97; color: #fff; }
        .spc__btn:disabled { opacity: .35; cursor: default; pointer-events: none; }

        /* Track */
        .spc__track-wrap { overflow: hidden; width: 100%; }
        .spc__track { display: flex; gap: 20px; transition: transform .5s cubic-bezier(.4,0,.2,1); width: max-content; }

        /* Track item width — same min() pattern as ProductsCarousel */
        .spc__track-item {
          --spc-pad: 80px;
          width: min(
            calc((1280px - var(--spc-pad) - (var(--per-page,3) - 1) * var(--gap,20px)) / var(--per-page,3)),
            calc((100vw  - var(--spc-pad) - (var(--per-page,3) - 1) * var(--gap,20px)) / var(--per-page,3))
          );
          flex-shrink: 0;
        }

        /* Card */
        .spc__card { background: #fff; border-radius: 10px; overflow: hidden; box-shadow: 0 2px 10px rgba(1,21,70,.09); cursor: pointer; display: flex; flex-direction: column; height: 100%; transition: transform .2s, box-shadow .2s; }
        .spc__card:hover { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(1,21,70,.14); }

        /* Media */
        .spc__card-media { position: relative; aspect-ratio: 1/1; overflow: hidden; background: #f0f2f5; flex-shrink: 0; }
        .spc__card-img { width: 100%; height: 100%; object-fit: cover; transition: transform .4s ease; display: block; }
        .spc__card:hover .spc__card-img { transform: scale(1.05); }
        .spc__card-img-fallback { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background: linear-gradient(135deg,#e8edf6,#d4ddf0); color: #a0aec0; }

        /* Badges */
        .spc__badges { position: absolute; top: 10px; left: 10px; display: flex; flex-direction: column; gap: 5px; }
        .spc__badge { display: inline-block; padding: 3px 9px; border-radius: 4px; font-size: 10.5px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; }
        .spc__badge--featured { background: #013d97; color: #fff; }
        .spc__badge--sale { background: #ef4444; color: #fff; }
        .spc__badge--out { background: #6b7280; color: #fff; }
        .spc__badge--low { background: #f59e0b; color: #fff; }

        /* Wishlist */
        .spc__wish { position: absolute; top: 10px; right: 10px; width: 32px; height: 32px; border-radius: 50%; background: rgba(255,255,255,.92); border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; box-shadow: 0 1px 4px rgba(0,0,0,.12); transition: background .2s, transform .2s; }
        .spc__wish:hover { background: #fff; transform: scale(1.12); }
        .spc__wish--active { background: #fff1f1; }

        /* Body */
        .spc__card-body { padding: 14px 16px 16px; display: flex; flex-direction: column; gap: 5px; flex: 1; }
        .spc__brand { font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: .06em; color: #029add; }
        .spc__name { font-size: 14px; font-weight: 700; color: #1a2236; line-height: 1.35; }
        .spc__spec { font-size: 12px; color: #6b7280; }
        .spc__spec strong { color: #013d97; }
        .spc__price-row { display: flex; align-items: baseline; gap: 8px; margin-top: 4px; }
        .spc__price { font-size: 16px; font-weight: 800; color: #1a2236; }
        .spc__price--sale { color: #013d97; }
        .spc__price--orig { font-size: 12px; font-weight: 500; color: #9ca3af; text-decoration: line-through; }

        /* ── CTA row: Add to Cart / Select options + WhatsApp ── */
        .spc__cta-row { margin-top: auto; padding-top: 10px; display: flex; gap: 8px; align-items: stretch; }
        .spc__cta { flex: 1; display: flex; align-items: center; justify-content: center; gap: 8px; background: #013d97; color: #fff; border: none; border-radius: 7px; padding: 10px 12px; font-family: inherit; font-size: 13px; font-weight: 700; cursor: pointer; transition: background .2s; min-width: 0; }
        .spc__cta:hover:not(:disabled) { background: #022d75; }
        .spc__cta:disabled { background: #e5e7eb; color: #9ca3af; cursor: not-allowed; }
        .spc__wa-btn { flex-shrink: 0; display: flex; align-items: center; justify-content: center; width: 40px; background: #25d366; color: #fff; border-radius: 7px; text-decoration: none; transition: background .18s, transform .18s; box-shadow: 0 2px 6px rgba(37,211,102,.28); }
        .spc__wa-btn:hover { background: #1da851; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(37,211,102,.38); }

        /* Dots */
        .spc__dots-wrap { display: flex; gap: 8px; justify-content: center; margin-top: 28px; }
        .spc__dot { width: 8px; height: 8px; border-radius: 50%; background: #d0d8ec; border: none; cursor: pointer; padding: 0; transition: background .2s, transform .2s; }
        .spc__dot--active { background: #f76b1c; transform: scale(1.3); }

        /* View all */
        .spc__view-all-wrap { text-align: center; margin-top: 32px; }
        .spc__view-all { display: inline-flex; align-items: center; gap: 8px; background: #f76b1c; color: #fff; font-size: 13px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; padding: 13px 28px; text-decoration: none; border-radius: 4px; transition: background .2s; }
        .spc__view-all:hover { background: #e05c10; }

        @media (max-width: 600px) {
          .spc { padding: 50px 0; }
          .spc__inner { padding: 0 20px; }
          .spc__track-item { --spc-pad: 40px; }
          .spc__header { flex-direction: column; align-items: flex-start; gap: 16px; }
          .spc__nav { align-self: flex-end; }
          .spc__cta-row { gap: 6px; }
          .spc__cta { font-size: 12px; padding: 10px 8px; }
          .spc__wa-btn { width: 36px; }
        }
      `}</style>
    </section>
  );
}

/* ─────────────────────────────────────────────
   HOME PAGE
───────────────────────────────────────────── */
function Home({ isLoggedIn, token, onAddToCart, onOpenAuth }) {
  const [aboutUs, setAboutUs] = useState(null);

  useEffect(() => {
    fetchJSON(`${API_BASE}/about.php`)
      .then(setAboutUs)
      .catch((err) => console.error("About Us load error:", err));
  }, []);

  return (
    <main>
      <Slideshow />
      <ShopProductsCarousel
        isLoggedIn={isLoggedIn}
        token={token}
        onAddToCart={onAddToCart}
        onOpenAuth={onOpenAuth}
      />
      <WhyChooseUs aboutUs={aboutUs} />
      <VideoSuppliersSection />
      <ServicesSection />
      <ProductsCarousel />

      <Footer />
    </main>
  );
}

export default Home;